/**
 * Calendar component showcase spec (dev only).
 *
 * A flat list of AuiNodes exercising the `Calendar` component across views
 * (Day / 5-day / Week), densities (compact / full) and both data sources
 * (inline `events` and a `eventsUrl` pointing at /public/dev/calendar-sample.json).
 *
 * The inline data is generated relative to the CURRENT week so that "today"
 * always falls within the data range — this keeps the live "now" line and the
 * "Today" button demonstrable whenever the page is opened.
 */

import type { AuiNode } from '@/types/canvases';

// ── Date scaffolding (relative to this week's Monday) ──────────────────────
const NOW = new Date();
function startOfWeekMon(d: Date): Date {
  const diff = (d.getDay() + 6) % 7; // days since Monday
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - diff);
}
const MONDAY = startOfWeekMon(NOW);
const pad = (n: number) => String(n).padStart(2, '0');
function dateOf(dayOffset: number): string {
  const d = new Date(MONDAY.getFullYear(), MONDAY.getMonth(), MONDAY.getDate() + dayOffset);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
/** ISO local datetime on day `dayOffset` (0=Mon) at h:m. */
function at(dayOffset: number, h: number, m = 0): string {
  return `${dateOf(dayOffset)}T${pad(h)}:${pad(m)}:00`;
}

const TODAY_OFFSET = (NOW.getDay() + 6) % 7; // 0=Mon … 6=Sun
const WED = dateOf(2);

// Mon–Sun, every day populated. Monday carries a 3-way overlap
// (Install Curtains / Vendor Call / Design Review) and Wednesday an all-day +
// a 7-participant meeting.
const INLINE_EVENTS: Record<string, unknown>[] = [
  { id: 'e1', title: 'Team Standup', start: at(0, 9), end: at(0, 9, 30), conferenceUrl: 'https://meet.google.com/abc-defg-hij', status: 'booked', owners: [{ name: 'Sophie Fischer', email: 'sophie.fischer@example.com' }, { name: 'Mia Schmidt', email: 'mia.schmidt@example.com' }, { name: 'Emma Peters', email: 'emma.peters@example.com' }], unread: true },
  { id: 'e2', title: 'Install Curtains', start: at(0, 10), end: at(0, 11, 30), location: '692 Cottage St SW, Unit 2', status: 'pending', owners: [{ name: 'Jon Doe' }] },
  { id: 'e3', title: 'Design Review', start: at(0, 10, 30), end: at(0, 11), location: 'Studio A', status: 'draft', owners: [{ name: 'Lena Becker' }] },
  { id: 'e3b', title: 'Vendor Call', start: at(0, 10, 15), end: at(0, 10, 45), status: 'pending', owners: [{ name: 'Otto Berg' }] },
  { id: 'e4', title: 'Lunch with Investor', start: at(0, 12, 30), end: at(0, 14), location: 'Noma', status: 'booked', owners: [{ name: 'Allen Woods', email: 'allen.woods@example.com' }], description: 'Discuss Series A terms and runway.' },
  { id: 'e10', title: 'HVAC Inspection', start: at(1, 9), end: at(1, 10), location: '4292 Dorothea Fork Apt. 563', status: 'draft', owners: [{ name: 'Verdie Dixon' }] },
  { id: 'e11', title: 'Attic Remodeling', start: at(1, 11), end: at(1, 12), location: '485 Leon Mill', status: 'pending', owners: [{ name: 'Jon Doe' }, { name: 'Jane Roe' }] },
  { id: 'e12', title: 'Install New Electrical Grid', start: at(1, 13), end: at(1, 14, 30), location: '62456 Wyman Square Suite 151', status: 'booked', owners: [{ name: 'Judy Holder' }] },
  { id: 'e20', title: 'All-hands', start: at(2, 0), allDay: true, status: 'booked', owners: [{ name: 'Sophie Fischer' }] },
  { id: 'e21', title: 'Sprint Kickoff', start: at(2, 10, 15), end: at(2, 12, 15), location: 'https://acme.zoom.us/j/9876543210', status: 'booked', owners: [{ name: 'Mia Schmidt', email: 'mia.schmidt@example.com' }, { name: 'Tom Klein', email: 'tom.klein@example.com' }, { name: 'Kara Nolan' }, { name: 'Otto Berg' }, { name: 'Pia Lang' }, { name: 'Rolf Adler' }, { name: 'Sven Falk' }] },
  { id: 'e22', title: 'Pipeline Review', start: at(2, 14), end: at(2, 15), status: 'done', owners: [{ name: 'Emma Peters' }] },
  { id: 'e30', title: 'Client Call', start: at(3, 9, 30), end: at(3, 10, 15), location: 'Phone', status: 'pending', owners: [{ name: 'Isabelle Bauer' }], unread: true },
  { id: 'e31', title: 'Lash Extensions', start: at(3, 11), end: at(3, 12), location: 'Salon', status: 'booked', owners: [{ name: 'Nina Koch' }] },
  { id: 'e32', title: 'Vendor Demo', start: at(3, 15), end: at(3, 16), status: 'cancelled', owners: [{ name: 'Marie Zimmermann' }] },
  { id: 'e40', title: 'Haarschnitt', start: at(4, 8, 30), end: at(4, 10), location: 'Salon', status: 'booked', owners: [{ name: 'Clara Weber' }] },
  { id: 'e41', title: 'Weekly Retro', start: at(4, 16, 30), end: at(4, 17, 30), location: 'Room 4', status: 'done', owners: [{ name: 'Sophie Fischer' }, { name: 'Mia Schmidt' }] },
  { id: 'e50', title: 'Farmers Market', start: at(5, 10), end: at(5, 11), location: 'Town Square', status: 'draft', owners: [{ name: 'Hannah Müller' }] },
  { id: 'e60', title: 'Sunday Brunch', start: at(6, 11), end: at(6, 12, 30), location: 'Café Central', status: 'booked', owners: [{ name: 'Allen Woods' }, { name: 'Hannah Müller' }] },
];

// A "happening now" event on today at the current hour, so the visible hour
// bounds include the present time and the red now-line is always on screen.
INLINE_EVENTS.push({
  id: 'e-now',
  title: 'Happening now',
  start: at(TODAY_OFFSET, NOW.getHours()),
  end: at(TODAY_OFFSET, Math.min(NOW.getHours() + 1, 23), NOW.getHours() >= 23 ? 59 : 0),
  status: 'booked',
  owners: [{ name: 'You' }],
});

// A very long description, to exercise the event-detail popover's viewport
// height cap + internal scroll (the panel must never run past the viewport).
INLINE_EVENTS.push({
  id: 'e-long',
  title: 'Weekly Engineering Sync',
  start: at(TODAY_OFFSET, 8),
  end: at(TODAY_OFFSET, 9),
  conferenceUrl: 'https://meet.example.com/eng-weekly-sync',
  status: 'booked',
  owners: [{ name: 'Alex Rivera', email: 'alex.rivera@example.com' }],
  description: [
    "'Scrum of scrums' sync between the Platform and App teams — a chance to surface",
    'cross-team dependencies, unblock work, and re-prioritize as needed.',
    '',
    '  - Are Platform and App out of sync on any in-flight items?',
    '  - Any high-priority App items blocked on Platform that need attention?',
    '  - Review in-progress epics',
    '  - Review flaky tests and CI health',
    '  - Review on-call handoff and open incidents',
    '',
    'There should always be at least one engineer from each team. Add guests to speak',
    'on specific issues on a meeting-by-meeting basis as needed.',
    '',
    'This is a working meeting — keep status updates in the doc and use the time for',
    'decisions and unblocking rather than read-outs.',
    '',
    'Agenda doc: https://example.com/docs/eng-weekly-sync',
    'Notes from last week: https://example.com/docs/eng-weekly-sync/last',
    '',
    'How to join',
    '-----------',
    'Join from your browser: https://meet.example.com/eng-weekly-sync',
    '    Passcode: 248135',
    '',
    'Dial-in (audio only):',
    '    +1 555 010 1234 (US) — conference ID 248 135 ##',
    '    +44 20 7946 0000 (UK) — conference ID 248 135 ##',
    '',
    'Recording and notes are posted to the #engineering channel afterwards.',
  ].join('\n'),
});

// The kitchen-sink event: every detail row populated AND a long description +
// a big guest list, so the popover is at its tallest. Exercises the viewport
// cap, internal scroll, sticky close button, and the guests list together.
INLINE_EVENTS.push({
  id: 'e-maxed',
  title: 'Quarterly Business Review — All Hands (Eng · Product · GTM · Support)',
  start: at(TODAY_OFFSET, 7),
  end: at(TODAY_OFFSET, 9),
  location: '1 Market St, Suite 3600, San Francisco, CA 94105 — Boardroom “Redwood”',
  conferenceUrl: 'https://meet.google.com/abc-defg-hij',
  status: 'booked',
  owners: [
    { name: 'Sophie Fischer', email: 'sophie.fischer@example.com' },
    { name: 'Mia Schmidt', email: 'mia.schmidt@example.com' },
    { name: 'Tom Klein', email: 'tom.klein@example.com' },
    { name: 'Kara Nolan', email: 'kara.nolan@example.com' },
    { name: 'Otto Berg', email: 'otto.berg@example.com' },
    { name: 'Pia Lang', email: 'pia.lang@example.com' },
    { name: 'Rolf Adler', email: 'rolf.adler@example.com' },
    { name: 'Sven Falk', email: 'sven.falk@example.com' },
    { name: 'Isabelle Bauer', email: 'isabelle.bauer@example.com' },
    { name: 'Nina Koch', email: 'nina.koch@example.com' },
    { name: 'Marie Zimmermann', email: 'marie.zimmermann@example.com' },
    { name: 'Hannah Müller', email: 'hannah.mueller@example.com' },
    { name: 'Allen Woods', email: 'allen.woods@example.com' },
    { name: 'Emma Peters', email: 'emma.peters@example.com' },
  ],
  description: [
    'Quarterly all-hands covering company performance, roadmap, and cross-functional asks.',
    'Please come prepared with your team updates and read the pre-read deck beforehand.',
    '',
    'Agenda',
    '======',
    '1. Welcome & OKR scorecard (10 min)',
    '2. Revenue & pipeline review — GTM (15 min)',
    '3. Product roadmap: shipped last quarter, committed next (20 min)',
    '4. Engineering health: reliability, velocity, on-call load (15 min)',
    '5. Support & customer escalations: themes and trends (10 min)',
    '6. People & hiring update (10 min)',
    '7. Open Q&A — submit questions in the doc ahead of time (20 min)',
    '',
    'Pre-reads',
    '---------',
    '- Q3 board deck (final): https://example.com/decks/q3-board',
    '- Reliability scorecard: https://example.com/reliability/q3',
    '- Hiring tracker: https://example.com/people/hiring',
    '',
    'Logistics',
    '---------',
    'In person in the Redwood boardroom; remote folks join via Google Meet.',
    'Lunch will be provided for on-site attendees at 12:00.',
    'Recording and notes will be shared in the #all-hands channel afterwards.',
    '',
    'If you cannot attend, please send a written update to your manager and watch the',
    'recording within 48 hours. This session counts toward the quarterly read-out.',
    '',
    'Thanks, and see you there!',
  ].join('\n'),
});

// A Google Calendar HTML description (bold, ordered lists, <br> breaks, a bare
// autolinked URL) — exercises MarkdownContent's raw-HTML path.
INLINE_EVENTS.push({
  id: 'e-html',
  title: 'Quarterly Planning Sync',
  start: at(TODAY_OFFSET, 13),
  end: at(TODAY_OFFSET, 14),
  conferenceUrl: 'https://example.zoom.us/j/1234567890',
  status: 'booked',
  owners: [{ name: 'Jordan Lee', email: 'jordan.lee@example.com' }],
  description:
    '<b>Agenda</b>:<br><ol><li>Last quarter - plan vs. actual</li><li>Retrospective highlights (key learnings and adjustments)</li><li>Roadmap for next quarter</li><li>Dependencies and risks</li></ol><br><br><b>Teams represented:</b><br><ol><li>Platform</li><li>Mobile</li><li>Data</li></ol><p><br>──────────<br><br>Hi there, <br><br>Jordan Lee is inviting you to a scheduled Zoom meeting. <br><br>Join from PC, Mac, Linux, iOS or Android: https://example.zoom.us/j/1234567890<br><br>Or Telephone:<br>    Dial: +1 555 010 1234 (US Toll)<br>    Meeting ID: 123 456 7890 <br>──────────</p>',
});

// An Outlook/Exchange plain-text description using the `LinkText<url>`
// convention (mailto/tel/http angle links + an [imageUrl]<url> inline image)
// — exercises normalizeDescriptionMarkup's link/image rewriting.
INLINE_EVENTS.push({
  id: 'e-outlook',
  title: '[EXTERNAL] Vendor Platform Demo',
  start: at(TODAY_OFFSET, 15),
  end: at(TODAY_OFFSET, 16),
  conferenceUrl: 'https://example.zoom.us/j/9876543210?pwd=AbCdEfGhIjKlMnOpQrStUvWx',
  status: 'booked',
  owners: [{ name: 'Robin Vendor', email: 'robin@vendor.example' }],
  description:
    'We are moving this meeting to accommodate schedules. On this meeting, @Robin Vendor, Sam<mailto:sam@vendor.example> will present a demo using project roles.\nThanks!\n\n[https://placehold.co/280x56/1e3a8a/ffffff/png?text=Vendor+Logo]<https://example.com/>\nJoin Zoom Meeting<https://example.zoom.us/j/9876543210?pwd=AbCdEfGhIjKlMnOpQrStUvWx>\nQuick Reference\nMeeting URL:\nhttps://example.zoom.us/j/9876543210?pwd=AbCdEfGhIjKlMnOpQrStUvWx\nMeeting ID:\n987 6543 210\nPasscode:\n123456\nOne tap mobile:\nUS: +15550109876,,9876543210#,,,,*123456#<tel:+15550109876,,9876543210#,,,,*123456#> or +15550104321,,9876543210#,,,,*123456#<tel:+15550104321,,9876543210#,,,,*123456#>\nJoin by Telephone\nDial:\n+1 555 010 0000 (US Toll Free)\nInternational numbers<https://example.zoom.us/u/abc123>\nSIP:\n9876543210@zoomcrc.example<mailto:9876543210@zoomcrc.example>\nPasscode:\n123456',
});

export const calendarShowcaseSpec: AuiNode[] = [
  {
    id: 'root',
    component: {
      type: 'Tabs',
      children: ['tab-day-full', 'tab-day-compact', 'tab-5day', 'tab-week-full', 'tab-week-compact', 'tab-24h-tz', 'tab-url'],
    },
  },

  // Day · full timeline (defaults to today → shows the live now-line)
  {
    id: 'tab-day-full',
    component: { type: 'Column', props: { label: 'Day · Timeline', gap: 4 }, children: ['cal-day-full'] },
  },
  {
    id: 'cal-day-full',
    component: { type: 'Calendar', props: { events: INLINE_EVENTS, view: 'day', density: 'full' } },
  },

  // Day · compact list
  {
    id: 'tab-day-compact',
    component: { type: 'Column', props: { label: 'Day · List', gap: 4 }, children: ['cal-day-compact'] },
  },
  {
    id: 'cal-day-compact',
    component: { type: 'Calendar', props: { events: INLINE_EVENTS, view: 'day', density: 'compact' } },
  },

  // 5-day · full
  {
    id: 'tab-5day',
    component: { type: 'Column', props: { label: '5-day · Timeline', gap: 4 }, children: ['cal-5day'] },
  },
  {
    id: 'cal-5day',
    component: { type: 'Calendar', props: { events: INLINE_EVENTS, view: 'fiveDay', density: 'full' } },
  },

  // Week · full
  {
    id: 'tab-week-full',
    component: { type: 'Column', props: { label: 'Week · Timeline', gap: 4 }, children: ['cal-week-full'] },
  },
  {
    id: 'cal-week-full',
    component: { type: 'Calendar', props: { events: INLINE_EVENTS, view: 'week', density: 'full' } },
  },

  // Week · compact columns
  {
    id: 'tab-week-compact',
    component: { type: 'Column', props: { label: 'Week · List', gap: 4 }, children: ['cal-week-compact'] },
  },
  {
    id: 'cal-week-compact',
    component: { type: 'Calendar', props: { events: INLINE_EVENTS, view: 'week', density: 'compact' } },
  },

  // 24-hour clock + two extra timezone gutter columns (anchored on Wednesday)
  {
    id: 'tab-24h-tz',
    component: { type: 'Column', props: { label: '24h + Timezones', gap: 4 }, children: ['cal-24h-tz'] },
  },
  {
    id: 'cal-24h-tz',
    component: {
      type: 'Calendar',
      props: {
        events: INLINE_EVENTS,
        view: 'day',
        density: 'full',
        date: WED,
        hour12: false,
        timezones: [
          { tz: 'America/New_York', label: 'NYC' },
          { tz: 'Asia/Tokyo', label: 'Tokyo' },
        ],
      },
    },
  },

  // Loaded from URL (exercises useVfsContent's plain-fetch path)
  {
    id: 'tab-url',
    component: { type: 'Column', props: { label: 'From URL', gap: 4 }, children: ['url-note', 'cal-url'] },
  },
  {
    id: 'url-note',
    component: {
      type: 'Text',
      props: {
        text: 'Loaded from `/dev/calendar-sample.json` via `eventsUrl` — the same code path agents use with `vfs:///calendar.json`.',
      },
    },
  },
  {
    id: 'cal-url',
    component: { type: 'Calendar', props: { eventsUrl: '/dev/calendar-sample.json', view: 'week', density: 'full' } },
  },
];
